// import pixMath from './pixMath.utils.js';
import pixUtils from './pix.utils.js';
import pixRegexUtils from '../regularExpressions/pixRegex.utils.js';
import regexConst from '@/consts/regex.const.js';
import Console from '@/shared/console';
import genericFunctions from './genericFunctions.utils.js';
import memoize from 'lodash.memoize';
// import { evaluate } from 'mathjs'; // has to be used for logical operations
import pixMath from '@/services/common/pixMath.utils.js';

/**
 * Evaluate a expresion from back with a function
 * example trunc(20+12) is equal to => 1
 *
 * @param {*} computedExpression
 * @returns
 */
const pixEvaluate = memoize(
  (computedExpression, shouldTransform = true) => {
    // fill with match of a function followed by (), trunc()
    const regexMatches = pixRegexUtils.getRegexMatches(regexConst.PIX_MATH_UTILS, computedExpression);
    let replacedExpression = computedExpression;
    if (regexMatches.length > 0) {
      regexMatches.forEach((match) => {
        const matchSplitByParenthesis = match.split('(');
        const customFunction = matchSplitByParenthesis[0];
        const argumentForCustomFunction = matchSplitByParenthesis[1].split(')')[0];
        replacedExpression = replacedExpression.replace(match, stringsEquivalences[customFunction](argumentForCustomFunction, shouldTransform));
      });
    } else {
      // The line below is parsing the result to number and was leading to unexpected results, such as parsing '0003' (as string) to 3 (as number)
      // replacedExpression = Number(pixMath.evaluate(computedExpression));
      replacedExpression = pixMath.evaluate(computedExpression);
    }

    if (replacedExpression === '' || replacedExpression === null) {
      return '';
    }

    return replacedExpression < 1e-6 ? 0 : replacedExpression;
  },
  (...args) =>
    Object.entries(args)
      .map(([_key, value]) => value)
      .join('_')
);

/**
 * TODO: Versión de evalConditions específica para los filters. Debería unificarse junto a la de pixEvaluateUtils.evalConditions.
 * Entre ambas, la diferencia radica en que los filtros guardan los selects y los multiselect en formato value = obj.valueCode en lugar de como el valor del valueCode
 * directamente.
 *
 */
const evalFilterConditions = (filter, filters, propertyToEvaluate) => {
  const filterReference = { ...filter };

  if (filterReference[propertyToEvaluate]) {
    let result;

    const internalFields = pixRegexUtils.getRegexMatches(regexConst.UNQUOTED_WHOLE_WORD, filterReference[propertyToEvaluate]);

    internalFields?.forEach((internalField) => {
      let reference;
      // Si llega una propiedad no anidada le suma .Value.ValueCode
      const unmutatedInternalField = internalField;

      if (!internalField.includes('.') || internalField.includes('ParentInfo.') || internalField.includes('Location.SearchType') || internalField.includes('IncomeExpenses.CustomFilterIsOnly12MonthIncomeExpense')) {
        internalField = `${internalField}.Value.ValueCode`;
      }

      let splittedInternalFieldStructure = internalField.split('.');
      if (internalField.includes('ParentInfo')) {
        splittedInternalFieldStructure[1] = `ParentInfo.${splittedInternalFieldStructure[1]}`;
        splittedInternalFieldStructure = splittedInternalFieldStructure.splice(1);
      }
      
      if (internalField.includes('IncomeExpenses')) {
        splittedInternalFieldStructure[1] = `IncomeExpenses.${splittedInternalFieldStructure[1]}`;
        splittedInternalFieldStructure = splittedInternalFieldStructure.splice(1);
      }

      splittedInternalFieldStructure.forEach((node, index) => {
        if (index === 0) {
          reference = filters.find(({ InternalField }) => InternalField === node);
        } else {
          if (reference?.hasOwnProperty(node)) {
            reference = reference[node];
          }
        }
      });

      if (Array.isArray(reference)) {
        reference = `[${reference.reduce((acc, { ValueCode }, index) => `${acc} ${ValueCode} ${index === reference.length - 1 ? '' : ','}`, '')}]`;

        if (reference === '[]') {
          reference = null;
        }
      } else if (reference & (typeof reference === 'object')) {
        reference = null;
      }

      filterReference[propertyToEvaluate] = filterReference[propertyToEvaluate].replaceAll(unmutatedInternalField, reference);
    });
    try {
      result = pixMath.evaluate(filterReference[propertyToEvaluate]);
    } catch (err) {
      Console.error(err);
    }

    return !!result;
  }

  return true;
};

const evalConditions = function (originalConditions, model, currentLang = null, form = null) {
  if (originalConditions && (model || form)) {
    let result = false;

    const internalFields = pixRegexUtils.getRegexMatches(regexConst.UNQUOTED_WHOLE_WORD, originalConditions);

    let conditions = originalConditions;

    internalFields?.forEach((internalField) => {
      /*
      So that conditions don't get replaced when they are substrings
      ie. "Price != null | AskingPrice != null" -> "100 != null | Asking100 != null"
      */
      const internalFieldWholeWordRegex = new RegExp(`\\b${internalField}\\b`, 'g');

      let fieldValue = form?.formData?.find(({ InternalField }) => InternalField === internalField)?.Value;

      if (model && fieldValue === undefined) {
        fieldValue = pixUtils.getFieldValue(internalField, model, null, null, currentLang);
      }

      /**
       * If the value's type is array (for instance, if the field is a multiselect), we are going to parse its values to the following format: [value_one, value_two, value_three]
       */
      if (Array.isArray(fieldValue)) {
        fieldValue = `[${fieldValue.reduce((acc, cv, index) => `${acc} ${cv} ${index === fieldValue.length - 1 ? '' : ','}`, '')}]`;

        if (fieldValue === '[]') {
          fieldValue = null;
        }
      } else if (typeof fieldValue === 'string') {
        // If the fieldValue's type is string, we surround it with quotation marks
        // ⛔ Closing == 'Closing'
        // ✅ 'Closing' == 'Closing'
        fieldValue = `"${fieldValue}"`;
      }

      conditions = conditions.replaceAll(internalFieldWholeWordRegex, fieldValue);
    });

    try {
      result = pixMath.evaluate(conditions);
    } catch (err) {
      Console.error(err);
    }

    return !!result;
  }

  return true;
};

const stringsEquivalences = {
  trunc: genericFunctions.pixTrunc,
  min: genericFunctions.pixMin,
  transformMonthsToYearsMonths: genericFunctions.transformMonthsToYearsMonths,
  transformYearsToYearsMonths: genericFunctions.transformYearsToYearsMonths,
  oneOutOfPrefix: genericFunctions.oneOutOfPrefix,
};

export default {
  pixEvaluate,
  evalFilterConditions,
  evalConditions,
};
